// src/components/dashboard/BaseDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Alert,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Chip,
  Switch,
  FormControlLabel,
  Button,
  Card,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

// DND Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente Sortable Widget (reutilizado de tu código existente)
const SortableWidget = ({ widget, isEditMode, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        height: '100%',
        border: isEditMode ? '2px dashed' : '1px solid',
        borderColor: isEditMode ? 'primary.main' : 'divider',
        transition: 'all 0.2s ease',
        cursor: isEditMode ? 'grab' : 'default',
        '&:hover': {
          boxShadow: isEditMode ? 4 : 2,
          borderColor: isEditMode ? 'primary.light' : 'divider',
        },
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Card>
  );
};

// Componente Base Dashboard
export const BaseDashboard = ({
  role,
  user,
  widgets,
  title,
  subtitle,
  roleIcon,
  roleColor = 'primary',
  onWidgetsChange,
  customActions = [],
  showEditMode = true,
  children
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = widgets.findIndex(widget => widget.id === active.id);
      const newIndex = widgets.findIndex(widget => widget.id === over.id);
      
      const newWidgets = arrayMove(widgets, oldIndex, newIndex);
      onWidgetsChange?.(newWidgets);
      setHasUnsavedChanges(true);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode && hasUnsavedChanges) {
      // Aquí puedes agregar lógica para guardar automáticamente
      setSnackbarMessage('Cambios guardados automáticamente');
      setSnackbarOpen(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleSave = () => {
    // Lógica para guardar los cambios
    setHasUnsavedChanges(false);
    setSnackbarMessage('Dashboard guardado exitosamente');
    setSnackbarOpen(true);
  };

  const handleReset = () => {
    // Lógica para resetear a configuración por defecto
    setSnackbarMessage('Dashboard restaurado a configuración por defecto');
    setSnackbarOpen(true);
    setHasUnsavedChanges(false);
  };

  return (
    <Container 
      maxWidth={isFullscreen ? false : "xl"} 
      sx={{ 
        py: 3,
        minHeight: '100vh',
        bgcolor: isFullscreen ? 'background.default' : 'transparent'
      }}
    >
      {/* Header del Dashboard */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h4" component="h1">
                {roleIcon} {title || `Dashboard ${role}`}
              </Typography>
              <Chip 
                label={role} 
                color={roleColor} 
                variant="outlined"
                size="small"
              />
            </Box>
            <Typography variant="body1" color="text.secondary">
              {subtitle || `Panel de control para ${role}`}
            </Typography>
          </Box>
          
          {showEditMode && (
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Chip
                label={`${widgets.length} widgets`}
                variant="outlined"
                size="small"
              />
              {isEditMode && (
                <Chip
                  label="Reorganizando"
                  color="primary"
                  size="small"
                  icon={<DragIcon />}
                />
              )}
              {hasUnsavedChanges && (
                <Chip
                  label="Sin guardar"
                  color="warning"
                  size="small"
                />
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    checked={isEditMode}
                    onChange={(e) => setIsEditMode(e.target.checked)}
                    size="small"
                  />
                }
                label="Reorganizar"
              />
            </Box>
          )}
        </Box>

        {/* Alertas */}
        {hasUnsavedChanges && (
          <Alert 
            severity="warning" 
            action={
              <Button color="inherit" size="small" onClick={handleSave}>
                Guardar
              </Button>
            }
            sx={{ mb: 2 }}
          >
            Tienes cambios sin guardar
          </Alert>
        )}

        {isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Modo edición activado. Arrastra los widgets para reorganizarlos.
            <Button size="small" onClick={handleReset} sx={{ ml: 2 }}>
              Restaurar por defecto
            </Button>
          </Alert>
        )}
      </Box>

      {/* Contenido personalizado adicional */}
      {children}

      {/* Grid de Widgets */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={widgets.map(w => w.id)}
          strategy={rectSortingStrategy}
        >
          <Grid container spacing={2}>
            {widgets.map((widget) => {
              const WidgetComponent = widget.component;
              
              return (
                <Grid 
                  item 
                  key={widget.id}
                  xs={widget.size?.xs || 12}
                  sm={widget.size?.sm || 6}
                  md={widget.size?.md || 4}
                  lg={widget.size?.lg || 3}
                >
                  <SortableWidget widget={widget} isEditMode={isEditMode}>
                    <Card sx={{ height: '100%' }}>
                      {widget.title && (
                        <Box sx={{ p: 2, pb: 1 }}>
                          <Typography variant="h6" component="h3">
                            {widget.title}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ p: 2, pt: widget.title ? 1 : 2 }}>
                        {WidgetComponent && (
                          <WidgetComponent 
                            user={user} 
                            userRole={role}
                            widget={widget}
                            {...widget.props}
                          />
                        )}
                      </Box>
                    </Card>
                  </SortableWidget>
                </Grid>
              );
            })}
          </Grid>
        </SortableContext>
      </DndContext>

      {/* SpeedDial para acciones rápidas */}
      {showEditMode && (
        <SpeedDial
          ariaLabel="Dashboard Actions"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<RefreshIcon />}
            tooltipTitle="Actualizar"
            onClick={() => window.location.reload()}
          />
          
          <SpeedDialAction
            icon={<EditIcon />}
            tooltipTitle={isEditMode ? "Salir de Edición" : "Editar"}
            onClick={toggleEditMode}
          />
          
          <SpeedDialAction
            icon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            tooltipTitle={isFullscreen ? "Salir Pantalla Completa" : "Pantalla Completa"}
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
          
          {customActions.map((action, index) => (
            <SpeedDialAction
              key={index}
              icon={action.icon}
              tooltipTitle={action.tooltip}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};
